<script setup lang="ts">
import { inject, ref, computed, watch, type Ref } from 'vue';
import { type Shift, type ShiftWithoutFinancialInformation } from '@/api/shifts';
import { type SerializedUser } from '@/api/users';
import { notesApi } from '@/api/notes';
import { taskRecordsApi } from '@/api/task-records';
import { incidentRecordsApi } from '@/api/incident-records';
import { medicationsApi } from '@/api/medications';
import { inventoryApi } from '@/api/inventory';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { usePermissions } from '@/composables/usePermissions';
import AvailableCaregiversList from '@/components/caregivers/available-caregivers-list.vue';
import InventoryMovementHistory from '@/components/inventory/inventory-movement-history.vue';
import NotesSection from '@/components/note/notes-section.vue';
import ShiftForm from './shift-form.vue';
import ShiftDestroyForm from './shift-destroy-form.vue';
import ShiftSuspendForm from './shift-suspend-form.vue';
import ShiftEditAttendances from './shift-edit-attendances.vue';
import ShiftShowGeneral from './shift-show-general.vue';
import ShiftShowAmounts from './shift-show-amounts.vue';
import ShiftShowReportImages from './shift-show-report-images.vue';
import ShiftShowReportTexts from './shift-show-report-texts.vue';
import ShiftShowSummary from './shift-show-summary.vue';
import ShiftShowVitalSigns from './shift-show-vital-signs.vue';
import ShiftShowTaskRecords from './shift-show-task-records.vue';
import ShiftTaskPlanSync from './shift-task-plan-sync.vue';
import ShiftShowIncidentRecords from './shift-show-incident-records.vue';
import ShiftShowMedications from './shift-show-medications.vue';
import ShiftShowHistory from './shift-show-history.vue';

const props = withDefaults(
  defineProps<{
    shift: Shift | ShiftWithoutFinancialInformation;
    initialTab?: 'general' | 'amounts' | 'report' | 'summary' | 'comments' | 'history';
    initialReportTab?: 'reportImages' | 'reportTexts' | 'vitalSigns' | 'taskRecords' | 'incidentRecords' |
      'medications';
  }>(),
  { initialTab: 'general', initialReportTab: 'reportImages' },
);

const emit = defineEmits(['close', 'refetch', 'success']);

const { hasPermission } = usePermissions();
const currentUser = inject<Ref<SerializedUser>>('currentUser');
const view = ref('show');

// Mirror TaskRecordPolicy#index? and IncidentRecordPolicy#index?: without these
// permissions the endpoints answer 401, so neither the query nor its tab exists.
const canReadTaskRecords = computed(() => hasPermission.value('tasks', 'index'));
const canReadIncidentRecords = computed(() => hasPermission.value('patients', 'read_medical_information'));
// Mirror the patient's Medicamentos and Inventario tabs: the company flag and the permissions.
const canReadMedications = computed(() => (
  !!currentUser?.value?.companyMedicationsEnabled && hasPermission.value('medications', 'index') &&
  hasPermission.value('patients', 'read_medical_information')
));
const canReadInventory = computed(() => (
  !!currentUser?.value?.companyInventoryEnabled && hasPermission.value('inventory', 'index')
));

// Opened on the shift's doses (the medications email links there) by someone who
// cannot read them, the dialog shows General instead.
const opensOnMedications = props.initialReportTab === 'medications';
const tab = ref(opensOnMedications && !canReadMedications.value ? 'general' : props.initialTab);

function allowedInitialReportTab() {
  const deniedTab = (props.initialReportTab === 'taskRecords' && !canReadTaskRecords.value) ||
    (props.initialReportTab === 'incidentRecords' && !canReadIncidentRecords.value);

  return deniedTab || opensOnMedications ? 'reportImages' : props.initialReportTab;
}

// Medicamentos and Inventario appear once their counts load, so the dialog opens
// on another tab and switches to Medicamentos when it was asked for (below).
const reportTab = ref<NonNullable<typeof props.initialReportTab> | 'inventory'>(
  allowedInitialReportTab(),
);

const { data: notes } = useQuery({
  queryKey: ['notes', 'shift', props.shift.id],
  queryFn: () => notesApi.index('shift', props.shift.id),
  enabled: computed(() => hasPermission.value('medical_information', 'index')),
});

const { data: taskRecords } = useQuery({
  queryKey: ['taskRecords', props.shift.id],
  queryFn: () => taskRecordsApi.indexByShift(props.shift.id),
  enabled: canReadTaskRecords,
});

// TaskRecordPolicy#sync?: whoever edits the patient's task plan, from the tasks tab.
const canSyncTaskPlan = computed(() => canReadTaskRecords.value && hasPermission.value('tasks', 'assign'));

const { data: taskPlanSync } = useQuery({
  queryKey: ['taskPlanSync', props.shift.id],
  queryFn: () => taskRecordsApi.planSync(props.shift.id),
  enabled: canSyncTaskPlan,
});

const { data: incidentRecords } = useQuery({
  queryKey: ['incidentRecords', props.shift.id],
  queryFn: () => incidentRecordsApi.indexByShift(props.shift.id),
  enabled: canReadIncidentRecords,
});

const { data: medicationDoses } = useQuery({
  queryKey: ['medicationDoses', props.shift.patientId, 'shift', props.shift.id],
  queryFn: () => medicationsApi.shiftDoses(props.shift.patientId, props.shift.id),
  enabled: canReadMedications,
});

// The same query the movements table runs, so it is fetched once.
const inventoryPlace = { patientId: props.shift.patientId, shiftId: props.shift.id };
const { data: inventoryMovements } = useQuery({
  queryKey: ['inventoryMovements', inventoryPlace],
  queryFn: () => inventoryApi.movements(inventoryPlace),
  enabled: canReadInventory,
});

// Counts leave anulados out; the tabs list them on demand.
const activeNotesCount = computed(() => notes.value?.filter(note => !note.voided).length || 0);
const hasTaskRecords = computed(() => (taskRecords.value?.length || 0) > 0);
// A shift in progress whose tasks differ from the patient's task plan: its tab
// shows even before the shift has any task, to offer bringing them in line.
const taskPlanChanged = computed(() => !!taskPlanSync.value?.inProgress &&
  taskPlanSync.value.toCreate + taskPlanSync.value.toRemove > 0);
const showTaskRecordsTab = computed(() => hasTaskRecords.value || taskPlanChanged.value);
const taskRecordsCount = computed(
  () => taskRecords.value?.filter(record => !record.voided).length || 0,
);
const incidentRecordsCount = computed(
  () => incidentRecords.value?.filter(record => !record.voided).length || 0,
);
// Doses due in the shift plus the SOS and extra doses given; the tab shows only when there are some.
const medicationsCount = computed(
  () => (medicationDoses.value?.doses.length || 0) + (medicationDoses.value?.unscheduled.length || 0),
);
const inventoryMovementsCount = computed(() => inventoryMovements.value?.length || 0);
// Once: switching tabs afterwards is the user's call.
let pendingMedicationsTab = opensOnMedications;
watch(medicationsCount, (count) => {
  if (!pendingMedicationsTab || count === 0) return;

  reportTab.value = 'medications';
  pendingMedicationsTab = false;
}, { immediate: true });
// The counter leaves anulados out; the tab still lists them on demand.
const showIncidentRecordsTab = computed(
  () => canReadIncidentRecords.value && (
    ('hasIncidents' in props.shift && props.shift.hasIncidents) || (incidentRecords.value?.length || 0) > 0
  ),
);
const canUpdateShift = computed(() => {
  if (props.shift.checkIn) {
    return hasPermission.value('marked_shifts', 'update');
  }

  return hasPermission.value('shifts', 'update');
});
const canDeleteShift = computed(() => {
  if (props.shift.checkIn) {
    return hasPermission.value('marked_shifts', 'destroy');
  }

  return hasPermission.value('shifts', 'destroy');
});
const canSuspendShift = computed(
  () => props.shift.canSuspend && hasPermission.value('shifts', 'update'),
);
const canReactivateShift = computed(
  () => props.shift.suspended && hasPermission.value('shifts', 'update'),
);

const queryClient = useQueryClient();

function onTaskPlanSynced() {
  queryClient.invalidateQueries({ queryKey: ['taskRecords', props.shift.id] });
  queryClient.invalidateQueries({ queryKey: ['taskPlanSync', props.shift.id] });
  emit('refetch');
}

function waitBeforeCloseAndRefetch() {
  const waitMs = 1500;
  setTimeout(() => {
    emit('refetch');
    emit('close');
  }, waitMs);
}

</script>
<template>
  <v-card
    v-if="view === 'show'"
    class="pa-4 shift-show-card"
  >
    <v-card-title class="mt-2 d-flex justify-space-between">
      Información del Turno
      <div>
        <v-btn
          v-if="canUpdateShift"
          icon="mdi-pencil"
          color="primary"
          size="small"
          variant="outlined"
          class="mr-2"
          @click="view = 'update'"
        />
        <v-btn
          v-if="props.shift.canEditAttendances && hasPermission('attendances', 'update')"
          icon="mdi-clock-edit"
          color="orange"
          size="small"
          variant="outlined"
          class="mr-2"
          @click="view = 'editAttendances'"
        />
        <v-btn
          v-if="canSuspendShift"
          icon="mdi-account-off"
          color="grey-darken-1"
          size="small"
          variant="outlined"
          class="mr-2"
          @click="view = 'suspend'"
        />
        <v-btn
          v-if="canReactivateShift"
          icon="mdi-account-check"
          color="grey-darken-1"
          size="small"
          variant="outlined"
          class="mr-2"
          @click="view = 'unsuspend'"
        />
        <v-btn
          v-if="canDeleteShift"
          icon="mdi-delete"
          color="red"
          size="small"
          variant="outlined"
          @click="view = 'destroy'"
        />
      </div>
    </v-card-title>
    <v-card class="shift-show-content">
      <v-tabs
        v-model="tab"
        color="primary"
      >
        <v-tab
          value="general"
          class="rounded-pill text-body-1"
        >
          General
        </v-tab>
        <v-tab
          v-if="hasPermission('shifts', 'read_financial_information')"
          value="amounts"
          class="rounded-pill text-body-1"
        >
          Precios
        </v-tab>
        <v-tab
          v-if="'reportTexts' in props.shift || 'reportImages' in props.shift"
          value="report"
          class="rounded-pill text-body-1"
        >
          Registros
        </v-tab>
        <v-tab
          v-if="'reportImagesSummary' in props.shift"
          value="summary"
          class="rounded-pill text-body-1"
        >
          Resumen
        </v-tab>
        <v-tab
          value="comments"
          class="rounded-pill text-body-1"
        >
          <div class="d-flex justify-space-between align-center ga-1">
            <span> Comentarios </span>
            <v-chip
              color="primary"
              size="small"
            >
              {{ activeNotesCount }}
            </v-chip>
          </div>
        </v-tab>
        <v-tab
          value="history"
          class="rounded-pill text-body-1"
        >
          Historial
        </v-tab>
      </v-tabs>
      <v-window v-model="tab">
        <v-window-item value="general">
          <shift-show-general :shift="props.shift" />
        </v-window-item>
        <v-window-item value="amounts">
          <shift-show-amounts
            v-if="tab === 'amounts'"
            :shift="(props.shift as Shift)"
          />
        </v-window-item>
        <v-window-item value="report">
          <template v-if="tab === 'report'">
            <v-divider class="mx-2 mb-3" />
            <v-tabs
              v-model="reportTab"
              color="primary"
              show-arrows
            >
              <v-tab
                value="reportImages"
                class="rounded-pill text-body-2"
              >
                Registros Visuales
              </v-tab>
              <v-tab
                value="reportTexts"
                class="rounded-pill text-body-2"
              >
                Registros Escritos
              </v-tab>
              <v-tab
                value="vitalSigns"
                class="rounded-pill text-body-2"
              >
                Signos Vitales
              </v-tab>
              <v-tab
                v-if="showTaskRecordsTab"
                value="taskRecords"
                class="rounded-pill text-body-2"
              >
                <div class="d-flex justify-space-between align-center ga-1">
                  <span>Registros de Tareas</span>
                  <v-chip
                    color="primary"
                    size="small"
                  >
                    {{ taskRecordsCount }}
                  </v-chip>
                </div>
              </v-tab>
              <v-tab
                v-if="showIncidentRecordsTab"
                value="incidentRecords"
                class="rounded-pill text-body-2"
              >
                <div class="d-flex justify-space-between align-center ga-1">
                  <span>Incidentes</span>
                  <v-chip
                    v-if="incidentRecordsCount > 0"
                    color="error"
                    size="small"
                  >
                    {{ incidentRecordsCount }}
                  </v-chip>
                </div>
              </v-tab>
              <v-tab
                v-if="medicationsCount > 0"
                value="medications"
                class="rounded-pill text-body-2"
              >
                <div class="d-flex justify-space-between align-center ga-1">
                  <span>Medicamentos</span>
                  <v-chip
                    color="primary"
                    size="small"
                  >
                    {{ medicationsCount }}
                  </v-chip>
                </div>
              </v-tab>
              <v-tab
                v-if="inventoryMovementsCount > 0"
                value="inventory"
                class="rounded-pill text-body-2"
              >
                <div class="d-flex justify-space-between align-center ga-1">
                  <span>Inventario</span>
                  <v-chip
                    color="primary"
                    size="small"
                  >
                    {{ inventoryMovementsCount }}
                  </v-chip>
                </div>
              </v-tab>
            </v-tabs>
            <v-window v-model="reportTab">
              <v-window-item value="reportImages">
                <shift-show-report-images
                  :shift="props.shift"
                  @voided="emit('refetch')"
                />
              </v-window-item>
              <v-window-item value="reportTexts">
                <shift-show-report-texts
                  v-if="reportTab === 'reportTexts'"
                  :shift="props.shift"
                  @voided="emit('refetch')"
                />
              </v-window-item>
              <v-window-item value="vitalSigns">
                <shift-show-vital-signs
                  v-if="reportTab === 'vitalSigns'"
                  :shift="props.shift"
                />
              </v-window-item>
              <v-window-item
                v-if="showTaskRecordsTab"
                value="taskRecords"
              >
                <shift-task-plan-sync
                  v-if="reportTab === 'taskRecords' && taskPlanSync && taskPlanChanged"
                  :shift-id="props.shift.id"
                  :sync="taskPlanSync"
                  @synced="onTaskPlanSynced"
                />
                <shift-show-task-records
                  v-if="reportTab === 'taskRecords'"
                  :task-records="taskRecords || []"
                />
              </v-window-item>
              <v-window-item
                v-if="showIncidentRecordsTab"
                value="incidentRecords"
              >
                <shift-show-incident-records
                  v-if="reportTab === 'incidentRecords'"
                  :incident-records="incidentRecords || []"
                />
              </v-window-item>
              <v-window-item
                v-if="medicationsCount > 0"
                value="medications"
              >
                <shift-show-medications
                  v-if="reportTab === 'medications' && medicationDoses"
                  :doses="medicationDoses"
                />
              </v-window-item>
              <v-window-item
                v-if="inventoryMovementsCount > 0"
                value="inventory"
              >
                <inventory-movement-history
                  v-if="reportTab === 'inventory'"
                  :place="inventoryPlace"
                  readonly
                  class="pa-2"
                />
              </v-window-item>
            </v-window>
          </template>
        </v-window-item>
        <v-window-item value="summary">
          <shift-show-summary
            v-if="tab === 'summary'"
            :shift="props.shift"
          />
        </v-window-item>
        <v-window-item
          value="comments"
          class="comments-window pa-4"
        >
          <notes-section
            v-if="tab === 'comments'"
            :noteable-type="'shift'"
            :noteable-id="props.shift.id"
          />
        </v-window-item>
        <v-window-item value="history">
          <shift-show-history
            v-if="tab === 'history'"
            :shift-id="props.shift.id"
          />
        </v-window-item>
      </v-window>
    </v-card>
    <v-card-actions>
      <v-dialog
        v-if="props.shift.canReplaceCaregiver"
        max-width="800"
        scrollable
      >
        <template #activator="{ props: activatorProps }">
          <v-btn
            v-bind="activatorProps"
            text="Ofrecer Turno"
            prepend-icon="mdi-account-multiple"
          />
        </template>
        <template #default>
          <available-caregivers-list :shift="(props.shift as Shift)" />
        </template>
      </v-dialog>
      <v-spacer />
      <v-btn
        text="Cerrar"
        @click="emit('close')"
      />
    </v-card-actions>
  </v-card>
  <shift-form
    v-if="view === 'update'"
    :shift="(props.shift as Shift)"
    :close-button-text="'Volver'"
    :date="props.shift.startDatetime"
    @close="view = 'show'"
    @success="emit('success')"
    @refetch="emit('refetch')"
  />
  <shift-destroy-form
    v-if="view === 'destroy'"
    :shift-id="props.shift.id"
    :is-marked-shift="!!props.shift.checkIn"
    :is-invoiced="!!props.shift.invoiced"
    :close-button-text="'Volver'"
    @close="waitBeforeCloseAndRefetch()"
    @success="emit('success')"
    @refetch="emit('refetch')"
  />
  <shift-edit-attendances
    v-if="view === 'editAttendances'"
    :shift="(props.shift as Shift)"
    @close="view = 'show'"
    @success="emit('success')"
    @refetch="emit('refetch')"
  />
  <shift-suspend-form
    v-if="view === 'suspend' || view === 'unsuspend'"
    :shift-id="props.shift.id"
    :mode="(view as 'suspend' | 'unsuspend')"
    :close-button-text="'Volver'"
    @close="waitBeforeCloseAndRefetch()"
    @success="emit('success')"
  />
</template>
<style scoped>
.shift-show-card {
  display: flex;
  flex-direction: column;
  max-height: 85vh;
}

.shift-show-content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.shift-show-content :deep(.v-tabs) {
  flex-shrink: 0;
}

.shift-show-content :deep(> .v-window) {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
</style>
